import { motion, useTransform } from 'framer-motion';
import { Scene, Counter, useSpan } from '../motion';
import { PLAYERS, SESSION } from '../demoData';

const METRICS = [
  { key: 'vpip', label: 'VPIP' },
  { key: 'pfr', label: 'PFR' },
  { key: 'threeBet', label: '3-bet' },
];
const SCALE = 60; // bar length = value / SCALE of the cell width
const FOCUS = 'Jun';
const rowSpan = i => [0.06 + i * 0.045, 0.34 + i * 0.045];

function MetricCell({ p, value, i }) {
  const [a, b] = rowSpan(i);
  const t = useSpan(p, a, b);
  const v = useTransform(t, x => x * value);
  return (
    <td className="py-[clamp(0.45rem,0.2rem+0.7*var(--u),0.9rem)] pr-3">
      <Counter value={v} className="pn-num block text-[clamp(0.85rem,0.6rem+0.8*var(--u),1.35rem)]" />
      <motion.span
        aria-hidden="true"
        className="mt-1.5 block h-[3px] origin-left rounded-full bg-pn-signal"
        style={{ width: `${(value / SCALE) * 100}%`, scaleX: t }}
      />
    </td>
  );
}

function Row({ p, player, i }) {
  const focus = useSpan(p, 0.62, 0.72);
  const isFocus = player.name === FOCUS;
  const bg = useTransform(focus, [0, 1], ['rgb(var(--pn-signal) / 0)', `rgb(var(--pn-signal) / ${isFocus ? 0.1 : 0})`]);
  const dim = useTransform(focus, [0, 1], [1, isFocus ? 1 : 0.45]);
  const [a] = rowSpan(i);
  const enter = useSpan(p, a - 0.04, a + 0.06);

  return (
    <motion.tr style={{ backgroundColor: bg, opacity: useTransform([enter, dim], ([e, d]) => e * d) }}>
      <td className="rounded-l-xl py-2 pl-3 pr-3 font-bold sm:pl-4">
        {player.name}
        {player.hero && <span className="pn-caption ml-2">(you)</span>}
      </td>
      <td className="pn-num hidden pr-4 text-pn-muted sm:table-cell">{player.hands}</td>
      {METRICS.map(m => (
        <MetricCell key={m.key} p={p} value={player[m.key]} i={i} />
      ))}
    </motion.tr>
  );
}

function ScaledCounter({ t, value }) {
  return <Counter value={useTransform(t, x => x * value)} />;
}

function Spotlight({ p }) {
  const player = PLAYERS.find(x => x.name === FOCUS);
  const t = useSpan(p, 0.62, 0.86);
  return (
    <motion.div style={{ opacity: useTransform(t, [0, 0.3], [0.25, 1]) }} className="mt-[clamp(1.25rem,0.5rem+2.5*var(--u),3rem)]">
      <p className="pn-caption">{player.name}, {player.hands} hands</p>
      <div className="mt-2 grid grid-cols-3 gap-4 border-t border-pn-line/10 pt-3">
        {METRICS.map(m => (
          <div key={m.key}>
            <p className="pn-num text-[clamp(1.9rem,0.8rem+3.6*var(--u),4.4rem)] font-bold leading-none text-pn-signal">
              <ScaledCounter t={t} value={player[m.key]} />
            </p>
            <p className="pn-caption mt-2">{m.label} %</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function PlayerStats() {
  return (
    <Scene id="players" length={2.4} label="Player stats">
      {p => (
        <div className="mx-auto flex h-full max-w-[1480px] flex-col justify-center px-4 pb-6 pt-20 sm:px-6 tall:px-12 land:grid land:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] land:items-center land:gap-16">
          <div>
            <h2 className="pn-h2 max-w-[12ch]">Every player, measured.</h2>
            <p className="pn-lede mt-4">VPIP, PFR and 3-bet for everyone at the table, from the same log.</p>
            <div className="hidden land:block tall:block">
              <Spotlight p={p} />
            </div>
          </div>

          <div className="mt-6 land:mt-0">
            <div className="pn-bezel">
              <div className="pn-plate overflow-hidden px-1 py-3 sm:px-3">
                <table className="w-full border-separate border-spacing-0 text-left text-[clamp(0.85rem,0.65rem+0.6*var(--u),1.2rem)]">
                  <caption className="pn-caption px-3 pb-3 text-left sm:px-4">
                    {SESSION.title} · {SESSION.hands} hands · example data
                  </caption>
                  <thead>
                    <tr className="pn-caption">
                      <th scope="col" className="pb-2 pl-3 font-normal sm:pl-4">Player</th>
                      <th scope="col" className="hidden pb-2 font-normal sm:table-cell">Hands</th>
                      {METRICS.map(m => (
                        <th key={m.key} scope="col" className="w-[24%] pb-2 font-normal">{m.label} %</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PLAYERS.map((player, i) => (
                      <Row key={player.name} p={p} player={player} i={i} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </Scene>
  );
}
